import { createRootRouteWithContext, createRoute, Outlet, redirect } from "@tanstack/react-router";
import { lazy } from "react";
import { z } from "zod";

import { defineAbilityFor } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { DocumentTypeSchema } from "@recobra/schemas/document";
import { TaskScopeSchema } from "@recobra/schemas/task-catalog";

import { AppLayout } from "./app-shell";
import { clientsAllQueryOptions } from "./clients";
import { RouteError } from "./layout";
import { queryClient } from "./lib";
import {
  defaultAuthedRoute,
  ensureAuthGroups,
  requireRouteAccess,
  type RouteAccessContext,
} from "./lib";
import { isAuthUnauthenticatedError } from "./lib";
import { casesApi, clientsApi, documentsApi, tasksApi } from "./lib/api";
import { taskAssigneesQueryOptions } from "./tasks";

const rootRoute = createRootRouteWithContext<RouteAccessContext>()({
  component: () => <Outlet />,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authed",
  component: AppLayout,
  errorComponent: RouteError,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  staticData: { getPageTitle: () => m.dashboard_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "Dashboard"]),
  component: lazy(() =>
    import("./dashboard/dashboard-page").then((m) => ({
      default: m.DashboardPage,
    })),
  ),
});

const casesIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cases",
  staticData: { getPageTitle: () => m.cases_title() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["read", "Case"]),
  component: lazy(() =>
    import("./cases/list").then((m) => ({
      default: m.CasesListPage,
    })),
  ),
});

const newCaseRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cases/new",
  staticData: { getPageTitle: () => m.case_new_title() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["create", "Case"]),
  validateSearch: z.object({
    clientId: z.uuid().optional(),
  }),
  component: lazy(() =>
    import("./cases/new-case-page").then((m) => ({
      default: m.NewCasePage,
    })),
  ),
});

const prefetchCaseDetailData = async (caseNumber: number, groups: readonly string[]) => {
  const kase = await queryClient.ensureQueryData({
    queryKey: ["cases", caseNumber],
    queryFn: () => casesApi.getCase(caseNumber),
  });
  void queryClient.prefetchQuery({
    queryKey: ["tasks", "case", kase.id],
    queryFn: () =>
      tasksApi.listTasks({
        contextType: "case",
        contextId: kase.id,
        limit: 100,
      }),
  });
  const ability = defineAbilityFor({ groups });
  if (ability.can("read", "Document")) {
    void queryClient.prefetchQuery({
      queryKey: ["cases", kase.id, "documents"],
      queryFn: () => documentsApi.listDocuments(kase.id),
    });
  }
  if (ability.can("viewHistory", "Case")) {
    void queryClient.prefetchQuery({
      queryKey: ["cases", kase.id, "history"],
      queryFn: () => casesApi.listCaseHistory(kase.id),
    });
  }
};

const caseDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cases/$caseNumber",
  staticData: { getPageTitle: () => m.case_title() },
  beforeLoad: async ({ context: { queryClient } }) => ({
    groups: await requireRouteAccess(queryClient, ["read", "Case"]),
  }),
  validateSearch: z.object({
    tab: z.enum(["overview", "contacts", "timeline", "flow", "documents", "payment"]).optional(),
    documentType: DocumentTypeSchema.optional(),
    historyEntryId: z.string().optional(),
  }),
  loader: ({ context: { groups }, params }) => {
    void prefetchCaseDetailData(Number(params.caseNumber), groups);
  },
  component: lazy(() =>
    import("./cases/detail-page").then((m) => ({
      default: m.CaseDetailPage,
    })),
  ),
});

const editCaseRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cases/$caseNumber/edit",
  staticData: { getPageTitle: () => m.case_edit_title() },
  beforeLoad: async ({ context: { queryClient }, params }) => {
    await requireRouteAccess(queryClient, ["update", "Case"]);
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- redirect() is the router API
    throw redirect({
      to: "/cases/$caseNumber",
      params,
      search: { tab: "overview" },
    });
  },
});

const finishCaseCreationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/cases/$caseNumber/finish-creation",
  staticData: { getPageTitle: () => m.case_complete_case_data_title() },
  beforeLoad: async ({ context: { queryClient }, params, preload }) => {
    await requireRouteAccess(queryClient, ["update", "Case"], ["create", "Case"]);
    if (preload) return;
    const kase = await queryClient.fetchQuery({
      queryKey: ["cases", Number(params.caseNumber)],
      staleTime: 0,
      queryFn: () => casesApi.getCase(Number(params.caseNumber)),
    });
    if (kase.creationCompletedAt) {
      return redirect({
        to: "/cases/$caseNumber",
        params,
        search: { tab: "overview" },
        replace: true,
      });
    }
  },
  component: lazy(() =>
    import("./cases/finish-case-creation-page").then((m) => ({
      default: m.FinishCaseCreationPage,
    })),
  ),
});

const clientsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/clients",
  staticData: { getPageTitle: () => m.clients_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["manage", "Client"]),
  component: lazy(() =>
    import("./clients/clients-list-page").then((m) => ({
      default: m.ClientsTable,
    })),
  ),
});

const newClientRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/clients/new",
  staticData: { getPageTitle: () => m.clients_add() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["manage", "Client"]),
  component: lazy(() =>
    import("./clients/new-client-page").then((m) => ({
      default: m.NewClientPage,
    })),
  ),
});

const clientDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/clients/$id",
  staticData: { getPageTitle: () => m.clients_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["manage", "Client"]),
  loader: ({ params }) => {
    const casesParams = {
      clientId: [params.id],
      q: undefined,
      status: undefined,
      caseType: undefined,
    };

    void queryClient.prefetchQuery({
      queryKey: ["clients", params.id],
      queryFn: () => clientsApi.get(params.id),
    });
    void queryClient.prefetchQuery({
      queryKey: ["cases", casesParams],
      queryFn: () => casesApi.listCases(casesParams),
    });
    void queryClient.prefetchQuery({
      queryKey: ["clients", params.id, "documents"],
      queryFn: () => documentsApi.listClientDocuments(params.id),
    });
  },
  component: lazy(() =>
    import("./clients/detail-page").then((m) => ({
      default: m.ClientDetailPage,
    })),
  ),
});

const editClientRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/clients/$id/edit",
  staticData: { getPageTitle: () => m.clients_action_edit() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["manage", "Client"]),
  component: lazy(() =>
    import("./clients/edit-client-page").then((m) => ({
      default: m.EditClientPage,
    })),
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/users",
  staticData: { getPageTitle: () => m.users_title() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["manage", "User"]),
  component: lazy(() =>
    import("./users/users-page").then((m) => ({
      default: m.UsersPage,
    })),
  ),
});

const newUserRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/users/new",
  staticData: { getPageTitle: () => m.users_create_title() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["manage", "User"]),
  component: lazy(() =>
    import("./users/new-user-page").then((m) => ({
      default: m.NewUserPage,
    })),
  ),
});

const userDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/users/$username",
  staticData: { getPageTitle: () => m.users_title() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["manage", "User"]),
  component: lazy(() =>
    import("./users/detail-page").then((m) => ({
      default: m.UserDetailPage,
    })),
  ),
});

const editUserRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/users/$username/edit",
  staticData: { getPageTitle: () => m.clients_action_edit() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["manage", "User"]),
  component: lazy(() =>
    import("./users/edit-user-page").then((m) => ({
      default: m.EditUserPage,
    })),
  ),
});

const notificationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/notifications",
  staticData: { getPageTitle: () => m.notifications_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "Notification"]),
  component: lazy(() =>
    import("./notifications/inbox-page").then((m) => ({
      default: m.NotificationInboxPage,
    })),
  ),
});

const noAccessRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/no-access",
  staticData: { getPageTitle: () => m.no_access_title() },
  // Authentication only, no permission gate: this is where callers with zero
  // recognized roles land (see defaultAuthedRoute), so it must never require
  // a permission they lack.
  beforeLoad: ({ context: { queryClient } }) => ensureAuthGroups(queryClient),
  component: lazy(() =>
    import("./layout/no-access-page").then((m) => ({
      default: m.NoAccessPage,
    })),
  ),
});

const tasksIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/tasks",
  staticData: { getPageTitle: () => m.tasks_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["readAll", "Task"]),
  validateSearch: z.object({
    taskId: z.uuid().optional(),
  }),
  loader: () => {
    void queryClient.prefetchQuery(taskAssigneesQueryOptions);
  },
  component: lazy(() =>
    import("./tasks/list-pages").then((m) => ({
      default: m.TasksListPage,
    })),
  ),
});

const myTasksRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/my-tasks",
  staticData: { getPageTitle: () => m.my_tasks_title() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["read", "Task"]),
  validateSearch: z.object({
    taskId: z.uuid().optional(),
  }),
  component: lazy(() =>
    import("./tasks/list-pages").then((m) => ({
      default: m.MyTasksListPage,
    })),
  ),
});

const calendarRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/calendar",
  staticData: { getPageTitle: () => m.calendar_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "Calendar"]),
  validateSearch: z.object({
    taskId: z.uuid().optional(),
  }),
  component: lazy(() =>
    import("./calendar").then((m) => ({
      default: m.CalendarPage,
    })),
  ),
});

const reportsCasesByClientRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/reports/cases-by-client",
  staticData: { getPageTitle: () => m.reports_cases_by_client_title() },
  beforeLoad: ({ context: { queryClient } }) => requireRouteAccess(queryClient, ["read", "Report"]),
  validateSearch: z.object({
    clientId: z.array(z.uuid()).optional(),
  }),
  loader: () => {
    void queryClient.prefetchQuery(clientsAllQueryOptions);
  },
  component: lazy(() =>
    import("./reports/cases-by-client").then((m) => ({
      default: m.CasesByClientReportPage,
    })),
  ),
});

const auditLogsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/audit-logs",
  staticData: { getPageTitle: () => m.audit_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "AuditLog"]),
  component: lazy(() =>
    import("./audit/audit-logs-page").then((m) => ({
      default: m.AuditLogsPage,
    })),
  ),
});

const taskSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/settings/tasks",
  staticData: { getPageTitle: () => m.task_settings_title() },
  validateSearch: z.object({ scope: TaskScopeSchema.optional() }),
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "TaskSettings"]),
  component: lazy(() =>
    import("./admin/task-catalog").then((module) => ({
      default: module.TaskCatalogPage,
    })),
  ),
});

const automationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/automations",
  staticData: { getPageTitle: () => m.automations_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "Automation"]),
  component: lazy(() =>
    import("./admin/automations").then((m) => ({
      default: m.AutomationsPage,
    })),
  ),
});

const automationRunsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/automations/runs",
  staticData: { getPageTitle: () => m.automation_runs_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "Automation"]),
  component: lazy(() =>
    import("./admin/automations").then((m) => ({
      default: m.AutomationRunsPage,
    })),
  ),
});

const flowRunRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/automations/runs/$runId",
  staticData: { getPageTitle: () => m.automation_runs_title() },
  beforeLoad: ({ context: { queryClient } }) =>
    requireRouteAccess(queryClient, ["read", "Automation"]),
  component: lazy(() =>
    import("./admin/automations").then((m) => ({
      default: m.AutomationRunPage,
    })),
  ),
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$",
  beforeLoad: async ({ context: { queryClient } }) => {
    let groups: readonly string[];
    try {
      groups = await ensureAuthGroups(queryClient);
    } catch (error) {
      if (isAuthUnauthenticatedError(error)) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- redirect() is the router API
        throw redirect({ to: "/" });
      }

      throw error;
    }

    // TanStack Router requires throwing the redirect return value.
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- redirect() is the router API
    throw redirect({ to: defaultAuthedRoute(groups) });
  },
});

export const routeTree = rootRoute.addChildren([
  authedRoute.addChildren([
    dashboardRoute,
    casesIndexRoute,
    newCaseRoute,
    caseDetailRoute,
    editCaseRoute,
    finishCaseCreationRoute,
    clientsRoute,
    newClientRoute,
    clientDetailRoute,
    editClientRoute,
    usersRoute,
    newUserRoute,
    userDetailRoute,
    editUserRoute,
    notificationsRoute,
    noAccessRoute,
    tasksIndexRoute,
    myTasksRoute,
    calendarRoute,
    reportsCasesByClientRoute,
    auditLogsRoute,
    automationsRoute,
    taskSettingsRoute,
    automationRunsRoute,
    flowRunRoute,
  ]),
  notFoundRoute,
]);
